import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ProgressTracker } from '../components/tax/ProgressTracker';
import { ArrowLeft, ArrowRight, Save, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { taxReturnsAPI } from '../lib/taxReturnsAPI';

import { LegacyBanner } from '../components/LegacyBanner';

export default function TaxQuestionnaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [taxReturn, setTaxReturn] = useState(null);
  const [entitySections, setEntitySections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const autoSaveTimerRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    personal_details: {},
    income: {},
    deductions: {},
    offsets: {},
    rental_properties: [],
    crypto: {},
    declaration: {}
  });

  useEffect(() => {
    loadData();
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [id]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!loading && taxReturn) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        handleSave(false);
      }, 30000);
    }
  }, [formData, loading]);

  const loadData = async () => {
    try {
      const [returnRes, questionnaireRes] = await Promise.all([
        taxReturnsAPI.getReturn(id),
        taxReturnsAPI.getQuestionnaire(id)
      ]);
      
      setTaxReturn(returnRes.data);

      // Fetch the entity-specific section list (B#4) — non-fatal if it fails.
      taxReturnsAPI.getSections(id)
        .then((res) => setEntitySections(res.data))
        .catch(() => {});

      // Load existing responses
      const existingData = questionnaireRes.data;
      setFormData({
        personal_details: existingData.personal_details || {},
        income: existingData.income || {},
        deductions: existingData.deductions || {},
        offsets: existingData.offsets || {},
        rental_properties: existingData.rental_properties || [],
        crypto: existingData.crypto || {},
        declaration: existingData.declaration || {}
      });
    } catch (error) {
      toast.error('Failed to load tax return');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (showToast = true) => {
    setSaving(true);
    try {
      await taxReturnsAPI.saveQuestionnaire(id, {
        ...formData,
        completed: false
      });
      
      if (showToast) {
        toast.success('Progress saved');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to save progress');
      }
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await taxReturnsAPI.saveQuestionnaire(id, {
        ...formData,
        completed: true
      });
      
      toast.success('Tax questionnaire submitted successfully');
      navigate('/tax-returns');
    } catch (error) {
      toast.error('Failed to submit questionnaire');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const nextStep = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
      handleSave(false); // Auto-save on step change
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    {
      title: 'Personal Details',
      description: 'Basic information and contact details'
    },
    {
      title: 'Income',
      description: 'All income sources for the tax year'
    },
    {
      title: 'Deductions',
      description: 'Work-related and other tax deductions'
    },
    {
      title: 'Offsets & Adjustments',
      description: 'Tax offsets and special adjustments'
    },
    {
      title: 'Rental Properties',
      description: 'Rental property income and expenses'
    },
    {
      title: 'Crypto & Investments',
      description: 'Cryptocurrency and capital gains'
    },
    {
      title: 'Declaration',
      description: 'Review and confirm your information'
    }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="questionnaire-title">
          Tax Return Questionnaire
        </h1>
        <p className="text-muted-foreground mt-1">
          {taxReturn?.client_name} - Tax Year {taxReturn?.tax_year}
          {taxReturn?.entity_type && <span className="ml-2 capitalize">· {taxReturn.entity_type.replace(/_/g, ' ')}</span>}
        </p>
      </div>

      {/* Entity-specific sections (B#4): shows which sections apply for this
          entity type, sourced from the backend questionnaire schema. */}
      {entitySections?.sections?.length > 0 && (
        <Card className="border-primary/20 bg-accent">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm font-medium text-foreground/90 mb-2">
              Sections for a <span className="capitalize">{(entitySections.entity_type || '').replace(/_/g, ' ')}</span> return:
            </p>
            <div className="flex flex-wrap gap-2">
              {entitySections.sections.map((s) => (
                <span key={s.key} className="text-xs bg-card border border-primary/20 text-primary rounded-full px-3 py-1">
                  {s.label}{s.repeatable ? ' (repeatable)' : ''}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Tracker */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6 pb-8">
          <div className="flex items-center justify-between mb-6">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                  index === currentStep
                    ? 'bg-accent0 border-primary text-white'
                    : index < currentStep
                    ? 'bg-accent0 border-primary text-white'
                    : 'bg-card border-border text-muted-foreground/70'
                }`}>
                  {index < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="font-semibold">{index + 1}</span>
                  )}
                </div>
                <span className={`text-xs mt-2 text-center ${
                  index === currentStep ? 'text-foreground font-semibold' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`hidden md:block absolute w-[calc((100%-80px)/6)] h-0.5 -mt-5 ml-[calc(50%+20px)] ${
                    index < currentStep ? 'bg-accent0' : 'bg-muted'
                  }`} style={{left: `${(index * 100) / 7}%`}} />
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              {steps[currentStep].title}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{steps[currentStep].description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card>
        <CardContent className="pt-6">
          {currentStep === 0 && <PersonalDetailsStep formData={formData.personal_details} updateField={(field, value) => updateField('personal_details', field, value)} />}
          {currentStep === 1 && <IncomeStep formData={formData.income} updateField={(field, value) => updateField('income', field, value)} />}
          {currentStep === 2 && <DeductionsStep formData={formData.deductions} updateField={(field, value) => updateField('deductions', field, value)} />}
          {currentStep === 3 && <OffsetsStep formData={formData.offsets} updateField={(field, value) => updateField('offsets', field, value)} />}
          {currentStep === 4 && <RentalStep formData={formData.rental_properties} setFormData={setFormData} />}
          {currentStep === 5 && <CryptoStep formData={formData.crypto} updateField={(field, value) => updateField('crypto', field, value)} />}
          {currentStep === 6 && <DeclarationStep formData={formData.declaration} updateField={(field, value) => updateField('declaration', field, value)} onSubmit={handleSubmit} />}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={prevStep}
          variant="outline"
          disabled={currentStep === 0 || saving}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <Button
          onClick={() => handleSave(true)}
          variant="outline"
          disabled={saving}
          data-testid="save-draft-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Draft'}
        </Button>

        {currentStep < 6 ? (
          <Button
            onClick={nextStep}
            className="bg-primary hover:bg-primary/90"
            data-testid="next-step-button"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary/90"
            disabled={saving}
            data-testid="submit-questionnaire-button"
          >
            Submit Questionnaire
            <CheckCircle className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Step Components
function PersonalDetailsStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="full_name">Full Legal Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name || ''}
            onChange={(e) => updateField('full_name', e.target.value)}
            placeholder="John Robert Smith"
            data-testid="full-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="dob">Date of Birth *</Label>
          <Input
            id="dob"
            type="date"
            value={formData.dob || ''}
            onChange={(e) => updateField('dob', e.target.value)}
            data-testid="dob-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tfn">Tax File Number (TFN) *</Label>
          <Input
            id="tfn"
            value={formData.tfn || ''}
            onChange={(e) => updateField('tfn', e.target.value)}
            placeholder="123 456 789"
            data-testid="tfn-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mobile">Mobile Number *</Label>
          <Input
            id="mobile"
            value={formData.mobile || ''}
            onChange={(e) => updateField('mobile', e.target.value)}
            placeholder="0400 000 000"
            data-testid="mobile-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">Residential Address *</Label>
        <Input
          id="address"
          value={formData.address || ''}
          onChange={(e) => updateField('address', e.target.value)}
          placeholder="123 Main Street, Sydney NSW 2000"
          data-testid="address-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="john.smith@example.com"
            data-testid="email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="residency">Residency Status *</Label>
          <Select value={formData.residency || ''} onValueChange={(value) => updateField('residency', value)}>
            <SelectTrigger id="residency" data-testid="residency-select">
              <SelectValue placeholder="Select residency status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resident">Australian Resident</SelectItem>
              <SelectItem value="foreign">Foreign Resident</SelectItem>
              <SelectItem value="working_holiday">Working Holiday Maker</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">Bank Account for Refund</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bank_bsb">BSB</Label>
            <Input
              id="bank_bsb"
              value={formData.bank_bsb || ''}
              onChange={(e) => updateField('bank_bsb', e.target.value)}
              placeholder="123-456"
              data-testid="bank-bsb-input"
            />
          </div>
          <div className="col-span-2 space-y-2">
            <Label htmlFor="bank_account">Account Number</Label>
            <Input
              id="bank_account"
              value={formData.bank_account || ''}
              onChange={(e) => updateField('bank_account', e.target.value)}
              placeholder="12345678"
              data-testid="bank-account-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function IncomeStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="salary">Salary or Wages</Label>
          <Input
            id="salary"
            type="number"
            step="0.01"
            value={formData.salary || ''}
            onChange={(e) => updateField('salary', e.target.value)}
            placeholder="0.00"
            data-testid="salary-input"
          />
          <p className="text-xs text-muted-foreground">From PAYG summaries</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="interest">Bank Interest</Label>
          <Input
            id="interest"
            type="number"
            step="0.01"
            value={formData.interest || ''}
            onChange={(e) => updateField('interest', e.target.value)}
            placeholder="0.00"
            data-testid="interest-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="dividends">Dividends (Unfranked)</Label>
          <Input
            id="dividends"
            type="number"
            step="0.01"
            value={formData.dividends || ''}
            onChange={(e) => updateField('dividends', e.target.value)}
            placeholder="0.00"
            data-testid="dividends-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="franked_dividends">Dividends (Franked)</Label>
          <Input
            id="franked_dividends"
            type="number"
            step="0.01"
            value={formData.franked_dividends || ''}
            onChange={(e) => updateField('franked_dividends', e.target.value)}
            placeholder="0.00"
            data-testid="franked-dividends-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="trust_distributions">Trust Distributions</Label>
          <Input
            id="trust_distributions"
            type="number"
            step="0.01"
            value={formData.trust_distributions || ''}
            onChange={(e) => updateField('trust_distributions', e.target.value)}
            placeholder="0.00"
            data-testid="trust-distributions-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rental_income">Rental Income</Label>
          <Input
            id="rental_income"
            type="number"
            step="0.01"
            value={formData.rental_income || ''}
            onChange={(e) => updateField('rental_income', e.target.value)}
            placeholder="0.00"
            data-testid="rental-income-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="capital_gains">Capital Gains</Label>
          <Input
            id="capital_gains"
            type="number"
            step="0.01"
            value={formData.capital_gains || ''}
            onChange={(e) => updateField('capital_gains', e.target.value)}
            placeholder="0.00"
            data-testid="capital-gains-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="foreign_income">Foreign Income</Label>
          <Input
            id="foreign_income"
            type="number"
            step="0.01"
            value={formData.foreign_income || ''}
            onChange={(e) => updateField('foreign_income', e.target.value)}
            placeholder="0.00"
            data-testid="foreign-income-input"
          />
        </div>
      </div>
      
      <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-xs text-blue-800">
          <strong>Note:</strong> Please ensure you upload supporting documents (PAYG summaries, bank statements, dividend statements) in the next step after completing this questionnaire.
        </div>
      </div>
    </div>
  );
}

function DeductionsStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="work_car_expenses">Car Expenses (Work-Related)</Label>
          <Input
            id="work_car_expenses"
            type="number"
            step="0.01"
            value={formData.work_car_expenses || ''}
            onChange={(e) => updateField('work_car_expenses', e.target.value)}
            placeholder="0.00"
            data-testid="car-expenses-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="travel_expenses">Travel Expenses</Label>
          <Input
            id="travel_expenses"
            type="number"
            step="0.01"
            value={formData.travel_expenses || ''}
            onChange={(e) => updateField('travel_expenses', e.target.value)}
            placeholder="0.00"
            data-testid="travel-expenses-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="home_office">Home Office Expenses</Label>
          <Input
            id="home_office"
            type="number"
            step="0.01"
            value={formData.home_office || ''}
            onChange={(e) => updateField('home_office', e.target.value)}
            placeholder="0.00"
            data-testid="home-office-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="self_education">Self-Education</Label>
          <Input
            id="self_education"
            type="number"
            step="0.01"
            value={formData.self_education || ''}
            onChange={(e) => updateField('self_education', e.target.value)}
            placeholder="0.00"
            data-testid="self-education-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="donations">Donations & Gifts</Label>
          <Input
            id="donations"
            type="number"
            step="0.01"
            value={formData.donations || ''}
            onChange={(e) => updateField('donations', e.target.value)}
            placeholder="0.00"
            data-testid="donations-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tax_agent_fees">Tax Agent Fees (Prior Year)</Label>
          <Input
            id="tax_agent_fees"
            type="number"
            step="0.01"
            value={formData.tax_agent_fees || ''}
            onChange={(e) => updateField('tax_agent_fees', e.target.value)}
            placeholder="0.00"
            data-testid="tax-agent-fees-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="investment_interest">Interest on Investments</Label>
          <Input
            id="investment_interest"
            type="number"
            step="0.01"
            value={formData.investment_interest || ''}
            onChange={(e) => updateField('investment_interest', e.target.value)}
            placeholder="0.00"
            data-testid="investment-interest-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="other_deductions">Other Deductions</Label>
          <Input
            id="other_deductions"
            type="number"
            step="0.01"
            value={formData.other_deductions || ''}
            onChange={(e) => updateField('other_deductions', e.target.value)}
            placeholder="0.00"
            data-testid="other-deductions-input"
          />
        </div>
      </div>
    </div>
  );
}

function OffsetsStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="private_health">Private Health Insurance</Label>
          <Select value={formData.private_health || ''} onValueChange={(value) => updateField('private_health', value)}>
            <SelectTrigger id="private_health" data-testid="private-health-select">
              <SelectValue placeholder="Do you have private health insurance?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="hecs_help">HECS/HELP Debt</Label>
          <Select value={formData.hecs_help || ''} onValueChange={(value) => updateField('hecs_help', value)}>
            <SelectTrigger id="hecs_help" data-testid="hecs-help-select">
              <SelectValue placeholder="Do you have HECS/HELP debt?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="medicare_levy_reduction">Medicare Levy Reduction</Label>
          <Select value={formData.medicare_levy_reduction || ''} onValueChange={(value) => updateField('medicare_levy_reduction', value)}>
            <SelectTrigger id="medicare_levy_reduction">
              <SelectValue placeholder="Claiming reduction?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="spouse_details">Spouse Details</Label>
          <Input
            id="spouse_details"
            value={formData.spouse_details || ''}
            onChange={(e) => updateField('spouse_details', e.target.value)}
            placeholder="Spouse name (if applicable)"
            data-testid="spouse-details-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="super_contributions">Personal Super Contributions</Label>
          <Input
            id="super_contributions"
            type="number"
            step="0.01"
            value={formData.super_contributions || ''}
            onChange={(e) => updateField('super_contributions', e.target.value)}
            placeholder="0.00"
            data-testid="super-contributions-input"
          />
        </div>
      </div>
    </div>
  );
}

function RentalStep({ formData, setFormData }) {
  const addProperty = () => {
    const newProperty = {
      id: Date.now().toString(),
      address: '',
      ownership_percentage: 100,
      rental_income: '',
      expenses: ''
    };
    setFormData(prev => ({
      ...prev,
      rental_properties: [...prev.rental_properties, newProperty]
    }));
  };

  const removeProperty = (id) => {
    setFormData(prev => ({
      ...prev,
      rental_properties: prev.rental_properties.filter(p => p.id !== id)
    }));
  };

  const updateProperty = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      rental_properties: prev.rental_properties.map(p =>
        p.id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Rental Properties</h3>
        <Button onClick={addProperty} size="sm" variant="outline" data-testid="add-property-button">
          Add Property
        </Button>
      </div>

      {formData.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No rental properties added</p>
          <p className="text-xs mt-1">Click "Add Property" if you have rental income</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.map((property, index) => (
            <Card key={property.id} className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  Property {index + 1}
                  <Button
                    onClick={() => removeProperty(property.id)}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Property Address</Label>
                  <Input
                    value={property.address || ''}
                    onChange={(e) => updateProperty(property.id, 'address', e.target.value)}
                    placeholder="123 Main St, Sydney NSW 2000"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Ownership %</Label>
                    <Input
                      type="number"
                      value={property.ownership_percentage || 100}
                      onChange={(e) => updateProperty(property.id, 'ownership_percentage', e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rental Income</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={property.rental_income || ''}
                      onChange={(e) => updateProperty(property.id, 'rental_income', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Expenses</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={property.expenses || ''}
                      onChange={(e) => updateProperty(property.id, 'expenses', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CryptoStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <LegacyBanner module="Income Tax" />
      <fieldset disabled className="contents">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-900">
          <strong>Important:</strong> Cryptocurrency transactions require mandatory manual review by an accountant. Please provide accurate information.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="has_crypto"
            checked={formData.has_crypto_activity || false}
            onCheckedChange={(checked) => updateField('has_crypto_activity', checked)}
            data-testid="has-crypto-checkbox"
          />
          <Label htmlFor="has_crypto" className="cursor-pointer">
            I had cryptocurrency transactions during this tax year
          </Label>
        </div>

        {formData.has_crypto_activity && (
          <div className="space-y-4 pl-6 border-l-2 border-primary">
            <div className="space-y-2">
              <Label htmlFor="crypto_exchanges">Exchanges Used</Label>
              <Input
                id="crypto_exchanges"
                value={formData.crypto_exchanges || ''}
                onChange={(e) => updateField('crypto_exchanges', e.target.value)}
                placeholder="e.g., Binance, CoinSpot, Coinbase"
                data-testid="crypto-exchanges-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="crypto_disposals">Number of Disposals/Transactions</Label>
              <Input
                id="crypto_disposals"
                type="number"
                value={formData.crypto_disposals || ''}
                onChange={(e) => updateField('crypto_disposals', e.target.value)}
                placeholder="0"
                data-testid="crypto-disposals-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="crypto_net_gain">Net Capital Gain/Loss</Label>
              <Input
                id="crypto_net_gain"
                type="number"
                step="0.01"
                value={formData.crypto_net_gain || ''}
                onChange={(e) => updateField('crypto_net_gain', e.target.value)}
                placeholder="0.00"
                data-testid="crypto-net-gain-input"
              />
              <p className="text-xs text-muted-foreground">You will need to upload exchange CSV or tax summary</p>
            </div>
          </div>
        )}
      </div>
      </fieldset>
    </div>
  );
}

function DeclarationStep({ formData, updateField, onSubmit }) {
  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle>Review Your Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-800">
            Before submitting, please review all the information you've provided. You can go back to any step to make changes.
            After submission, you'll need to upload supporting documents.
          </p>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">Declaration</h3>
        
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Checkbox
              id="declaration_complete"
              checked={formData.declaration_complete || false}
              onCheckedChange={(checked) => updateField('declaration_complete', checked)}
              data-testid="declaration-complete-checkbox"
            />
            <Label htmlFor="declaration_complete" className="cursor-pointer leading-relaxed">
              I declare that the information provided is true and complete to the best of my knowledge
            </Label>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="declaration_records"
              checked={formData.declaration_records || false}
              onCheckedChange={(checked) => updateField('declaration_records', checked)}
              data-testid="declaration-records-checkbox"
            />
            <Label htmlFor="declaration_records" className="cursor-pointer leading-relaxed">
              I have kept proper records and can provide evidence for all claims
            </Label>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="declaration_consent"
              checked={formData.declaration_consent || false}
              onCheckedChange={(checked) => updateField('declaration_consent', checked)}
              data-testid="declaration-consent-checkbox"
            />
            <Label htmlFor="declaration_consent" className="cursor-pointer leading-relaxed">
              I consent to my accountant reviewing this information and preparing my tax return
            </Label>
          </div>
        </div>
      </div>

      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-900">
          <strong>Next Steps:</strong> After submitting this questionnaire, you'll be prompted to upload supporting documents 
          (PAYG summaries, bank statements, receipts, etc.). Our AI will extract data from your documents and our accountants 
          will review everything before finalizing your tax return.
        </p>
      </div>
    </div>
  );
}
